/**
 * LandingFooter - Minimal footer for landing page
 *
 * Contains:
 * - Logo + Tagline
 * - Navigation links (Try Free / Log in or Go to app)
 * - Resources (How it Works, FAQ)
 * - Legal (Terms, Privacy, Contact)
 * - Copyright
 */

"use client";

import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import Logo from "@/components/icons/Logo";
import { isRegistrationEnabled } from "@/lib/feature-flags";

interface LandingFooterProps {
  isAuthenticated: boolean;
}

export default function LandingFooter({ isAuthenticated }: LandingFooterProps) {
  const t = useTranslations("landing.footer");
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative z-10 border-t border-gray-700/50 py-12">
      <div className="absolute inset-0 bg-white/5 backdrop-blur-xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
          {/* Logo + Tagline */}
          <div className="col-span-2 md:col-span-1 flex flex-col items-center md:items-start">
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-8 h-8 rounded-md flex items-center justify-center">
                <Logo />
              </div>
              <span className="text-white text-xl font-bold tracking-tight">
                RigCraft
              </span>
            </div>
            <p className="text-gray-400 text-sm text-center md:text-left">
              {t("tagline")}
            </p>
          </div>

          {/* Navigation Links */}
          <div className="text-center md:text-left">
            <h3 className="text-white font-medium mb-3">{t("navigation")}</h3>
            <ul className="space-y-2 text-gray-400">
              {isAuthenticated ? (
                <li>
                  <Link
                    href="/build-preset"
                    className="hover:text-white transition-colors"
                  >
                    {t("goToApp")}
                  </Link>
                </li>
              ) : (
                <>
                  {isRegistrationEnabled && (
                    <li>
                      <Link
                        href="/register"
                        className="hover:text-white transition-colors"
                      >
                        {t("tryFree")}
                      </Link>
                    </li>
                  )}
                  <li>
                    <Link
                      href="/login"
                      className="hover:text-white transition-colors"
                    >
                      {t("login")}
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Resources */}
          <div className="text-center md:text-left">
            <h3 className="text-white font-medium mb-3">{t("resources")}</h3>
            <ul className="space-y-2 text-gray-400">
              <li>
                <Link
                  href="/how-it-works"
                  className="hover:text-white transition-colors"
                >
                  {t("howItWorks")}
                </Link>
              </li>
              <li>
                <Link
                  href="/faq"
                  className="hover:text-white transition-colors"
                >
                  {t("faq")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div className="text-center md:text-left">
            <h3 className="text-white font-medium mb-3">{t("legal")}</h3>
            <ul className="space-y-2 text-gray-400">
              <li>
                <Link
                  href="/terms"
                  className="hover:text-white transition-colors"
                >
                  {t("terms")}
                </Link>
              </li>
              <li>
                <Link
                  href="/privacy"
                  className="hover:text-white transition-colors"
                >
                  {t("privacy")}
                </Link>
              </li>
              <li>
                <Link
                  href="/contact"
                  className="hover:text-white transition-colors"
                >
                  {t("contact")}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="text-center border-t border-gray-800 pt-6">
          <p className="text-sm text-gray-500">
            {t("copyright", { year: currentYear })}
          </p>
        </div>
      </div>
    </footer>
  );
}
